import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  MenuItem,
} from '@mui/material';
import { SalesReturnService } from '../../services/salesReturn.service';
import { RETURN_STATUS_COLORS, SalesReturnStatus, SalesReturnSummary } from '../../interfaces/sales/salesReturn.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import SalespersonSelect, { salespersonParams } from './scope/SalespersonSelect';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const STATUSES: SalesReturnStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

/** Customer returns. A return is recorded from a posted delivery challan ("Record return" on the challan). */
const SalesReturnList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [owner, setOwner] = useState<string | null>(null);
  const institutionId = AuthService.getSelectedInstitution();
  const [rows, setRows] = useState<SalesReturnSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await SalesReturnService.list(institutionId!, { status: status || undefined, ...salespersonParams(owner) }));
    } catch (e) {
      toast.showApiError(e, 'Failed to load customer returns');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status, owner]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.returnList.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Customer Returns</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="sales.returnList.statusFilter" select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 260 }}
          onChange={e => setParams(e.target.value ? { status: e.target.value } : {})}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
        <SalespersonSelect data-help="sales.returnList.filter.salesperson" withUnassigned sx={{ flex: '1 1 220px', maxWidth: 320 }} value={owner} onChange={setOwner} />
      </Box>
      {loading ? <CircularProgress /> : (
        <Paper data-help-section="sales.returnList.table">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell data-help="sales.returnList.col.number">Return</TableCell><TableCell data-help="sales.returnList.col.date">Date</TableCell><TableCell data-help="sales.returnList.col.customer">Customer</TableCell><TableCell data-help="sales.returnList.col.salesperson">Salesperson</TableCell><TableCell data-help="sales.returnList.col.challan">Challan</TableCell>
                <TableCell data-help="sales.returnList.col.status">Status</TableCell><TableCell align="right" data-help="sales.returnList.col.restocked">Restocked</TableCell><TableCell align="right" data-help="sales.returnList.col.damaged">Damaged</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={8}>No customer returns.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/returns/${r.id}`)}>
                    <TableCell>{r.returnNo ?? 'Draft'}</TableCell>
                    <TableCell>{formatDate(r.returnDate)}</TableCell>
                    <TableCell>{r.customerName}</TableCell>
                    <TableCell>{r.salespersonName ?? 'Unassigned'}</TableCell>
                    <TableCell>{r.challanNo}</TableCell>
                    <TableCell><Chip data-help="sales.returnList.status" size="small" label={humanizeEnum(r.status)} color={RETURN_STATUS_COLORS[r.status]} /></TableCell>
                    <TableCell align="right">{formatMoney(r.restockValue)}</TableCell>
                    <TableCell align="right">{formatMoney(r.damagedValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default SalesReturnList;
